import { ListingStatus } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import { PlanetCardInfoType, PlanetLite, PlanetStage } from '@apeiron/planet'
import { SkeletonBox, useMessageContext } from '@apeiron/temp'
import { ApeironSimpleButton } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import PlanetSelectCardOnRental from '@src/components/orbitalTrack/attach/PlanetSelectCardOnRental'
import PlanetSelectPopup, {
  FetchParamsCallback
} from '@src/components/planet/list/PlanetSelectPopup'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import { PlanetTypeProvider } from '@src/contexts/planet/PlanetTypeContext'
import { GetMyPlanetsRequest } from '@src/hooks/graphql/useGetMyPlanets'
import {
  PlanetLiteDeserializeMode,
  PlanetLiteDeserializeOption
} from '@src/types/deserialize'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
`

const OverallContainer = styled(Box)`
  display: flex;
  margin: 0px 30px;
  gap: 10px;
  justify-content: space-between;
  ${props => props.theme.breakpoints.down('res480')} {
    flex-wrap: wrap;
  }
`

const OverallInfoContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
`

const OverallInfo = styled(Typography)`
  color: #b5b5b5;
  display: inline;
  font-weight: 300;
  line-height: 24px;
  white-space: pre-line;
`

const OrangeInfo = styled(OverallInfo)`
  color: #ffbf36;
  font-weight: 500;
`

const CleanButton = styled(ApeironSimpleButton)`
  background-color: #181819;
  border: 1px solid white;
  border-radius: 2px;
  font-weight: 300;
  white-space: nowrap;
  :hover {
    background-color: #181819;
    filter: brightness(140%);
  }
  :disabled {
    background-color: #535556;
    border: unset;
    color: #a0a0a0;
  }
  ${props => props.theme.breakpoints.up('res1280')} {
    height: unset;
    padding: 7px 50px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    height: 40px;
    padding: 10px 50px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    margin-left: auto;
  }
`

const PlanetsContainer = styled(SkeletonBox)`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  justify-content: center;
  margin: 0px 20px;
  min-height: 100px;
`

const PlanetContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
`

const PlanetCard = styled(PlanetSelectCardOnRental)`
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 200px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 140px;
  }
  .Custom-Overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
`

const PlanetAge = styled(Typography)`
  color: white;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-align: center;
  white-space: pre-line;
  width: 100%;
  strong {
    color: #1d66bf;
    font-size: 18px;
    font-weight: 500;
  }
`

const PlanetSelectExceedWarning = styled(Typography)`
  font-size: 16px;
  font-weight: 300;
  white-space: pre-line;
  strong {
    color: #ffbf00;
    font-size: 16px;
    font-weight: 500;
    line-height: 30px;
  }
`

const NOT_CHANGING_PLANET = -1

const OrbitalTrackSelectPlanet: FC<Props> = props => {
  const { className, isOwner } = props

  const {
    availableTracks,
    done,
    filledPlanets,
    selectedPlanets,
    star,
    trackDetail,
    setting,
    setAndFillSelectedPlanets
  } = useOrbitalTrackAttachContext()

  const { setInfo } = useMessageContext()

  const { config } = useFrontendConfigContext()

  const [openSelectPlanetPopup, setOpenSelectPlanetPopup] = useState(false)

  const [changeIndex, setChangeIndex] = useState<number>(NOT_CHANGING_PLANET)

  const { t } = useTranslation()

  const handleOnAdd = useCallback(() => {
    setChangeIndex(NOT_CHANGING_PLANET)
    setOpenSelectPlanetPopup(true)
  }, [])

  const handleOnChange = useCallback(
    (planet: PlanetLite) => {
      const index = R.findIndex(
        R.propEq(planet.planetID, 'planetID'),
        filledPlanets
      )

      setChangeIndex(index)
      setOpenSelectPlanetPopup(true)
    },
    [filledPlanets]
  )

  const handleOnRemove = useCallback(
    (planet: PlanetLite) => {
      const result = R.reject(
        R.propEq(planet.planetID, 'planetID'),
        filledPlanets
      )

      setInfo({
        cancel: t('common.cancel'),
        confirm: t('common.popup.remove'),
        title: t('common.popup.confirm_remove'),
        onConfirm: () => {
          setAndFillSelectedPlanets(result)
          setInfo(null)
        }
      })
    },
    [filledPlanets, setAndFillSelectedPlanets, setInfo, t]
  )

  const handleOnFetchParams: FetchParamsCallback = useCallback(async () => {
    const getMyPlanetsRequest: GetMyPlanetsRequest = {
      stage: [PlanetStage.Planet],
      nftListingStatus: ListingStatus.UNLISTING
    }

    const getMyPlanetsOption: PlanetLiteDeserializeOption = {
      mode: PlanetLiteDeserializeMode.ATTACH_TO_ORBIT,
      cooldown: config?.attach.cooldown || 0
    }

    return [getMyPlanetsRequest, getMyPlanetsOption]
  }, [config?.attach.cooldown])

  const handleOnClose = useCallback(() => {
    setOpenSelectPlanetPopup(false)
  }, [])

  const handleOnDisablePlanet = useCallback(
    (planet: PlanetLite): string | null => {
      if (!R.isNil(star) && !R.isNil(trackDetail)) {
        const { planetID } = planet

        if (R.includes(planetID, R.pluck('planetID', filledPlanets))) {
          return t('planet.card.status.already_selected')
        }
      }

      return null
    },
    [filledPlanets, star, trackDetail, t]
  )

  const handleOnClean = useCallback(() => {
    setAndFillSelectedPlanets([])
  }, [setAndFillSelectedPlanets])

  const handleOnSelect = useCallback(
    (planets: PlanetLite[]) => {
      if (changeIndex !== NOT_CHANGING_PLANET) {
        const planet = R.head(planets)

        if (!R.isNil(planet)) {
          setAndFillSelectedPlanets(
            R.update(changeIndex, planet, filledPlanets)
          )
        }
      } else {
        setAndFillSelectedPlanets(R.concat(filledPlanets, planets))
      }

      setOpenSelectPlanetPopup(false)
    },
    [changeIndex, filledPlanets, setAndFillSelectedPlanets]
  )

  const aging = useMemo(() => {
    return (star?.buff.agingBuff || 0) + (star?.additionBuff.agingBuff || 0)
  }, [star])

  const ageToDay = t('godiverse.orbital_track.age_to_day', {
    count: aging
  })

  const ageDuration = R.isNil(setting)
    ? ''
    : t('godiverse.orbital_track.duration', {
        count: aging * (setting?.rentalDuration || 0)
      })

  return (
    <PlanetTypeProvider>
      <Container className={className}>
        <OverallContainer>
          <OverallInfoContainer>
            <OverallInfo>
              <Trans
                components={{
                  orange: <OrangeInfo />
                }}
              >
                {isOwner ? ageToDay : `${ageToDay} \n ${ageDuration}`}
              </Trans>
            </OverallInfo>
          </OverallInfoContainer>
          <CleanButton
            disabled={R.isEmpty(filledPlanets) || done}
            onClick={handleOnClean}
          >
            {t('common.button.clear_all')}
          </CleanButton>
        </OverallContainer>
        <PlanetsContainer loading={R.isNil(star) || R.isNil(trackDetail)}>
          {RA.mapIndexed((planet: PlanetLite | null, index: number) => {
            const key = planet ? planet.planetID : `blank-${index}`

            return (
              <PlanetContainer>
                <PlanetCard
                  disableSelect={done}
                  infoType={PlanetCardInfoType.Age}
                  key={key}
                  planet={planet}
                  onAdd={handleOnAdd}
                  onChange={handleOnChange}
                  onRemove={handleOnRemove}
                />
                {!R.isNil(planet) && (
                  <PlanetAge>
                    <Trans>
                      {t('godiverse.orbital_track.current_age', {
                        age: planet.age
                      })}
                    </Trans>
                  </PlanetAge>
                )}
              </PlanetContainer>
            )
          }, selectedPlanets)}
        </PlanetsContainer>
        <PlanetSelectPopup
          cardInfoType={PlanetCardInfoType.Age}
          open={openSelectPlanetPopup}
          selectLimit={
            changeIndex === NOT_CHANGING_PLANET ? availableTracks : 1
          }
          selectLimitExceedMessage={
            <PlanetSelectExceedWarning>
              <Trans>
                {t('godiverse.orbital_track.attach_planet.exceed_track_limit')}
              </Trans>
            </PlanetSelectExceedWarning>
          }
          deriveFetchParams={handleOnFetchParams}
          disablePlanet={handleOnDisablePlanet}
          onClose={handleOnClose}
          onSelect={handleOnSelect}
        />
      </Container>
    </PlanetTypeProvider>
  )
}

type Props = {
  className?: string
  isOwner: boolean
}

export default OrbitalTrackSelectPlanet
